import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SPRINT_3_CONTENT } from '../data/course-data';

@Component({
  selector: 'app-sprint3-section',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
      <!-- Section Header -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl relative overflow-hidden">
        <div class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold mb-3 border border-[#F27D26]/30">
          <span class="material-symbols-outlined text-sm">published_with_changes</span>
          <span>SPRINT 3 (10h) • O PROTOCOLO ÁGIL</span>
        </div>
        <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {{ content.title }}
        </h2>
        <p class="text-zinc-400 text-sm max-w-3xl">
          {{ content.subtitle }} — Como aplicar a mentalidade Lean na engenharia de redes para lançar produtos viáveis rápido e aprender com dados reais dos clientes.
        </p>
      </div>

      <!-- Loop Lean Startup Simulator -->
      <div class="bg-blueprint-grid rounded-2xl border border-white/10 p-6 sm:p-8 text-white shadow-xl space-y-8">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <span class="text-xs font-mono font-medium text-[#F27D26] uppercase tracking-wider block mb-1">
              • O CICLO CONTÍNUO DE VALIDAÇÃO
            </span>
            <h3 class="text-xl sm:text-2xl font-bold text-white flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26]">sync</span>
              <span>O Loop Lean Startup</span>
            </h3>
            <p class="text-xs text-zinc-400 mt-1">{{ content.loopObjective }}</p>
          </div>

          <button 
            type="button"
            (click)="advanceLoopStep()"
            class="px-5 py-2.5 rounded-xl bg-[#F27D26] hover:bg-[#F27D26]/90 text-black font-bold text-xs transition-all shadow-lg shadow-[#F27D26]/20 flex items-center space-x-2 shrink-0 cursor-pointer">
            <span class="material-symbols-outlined text-sm">play_arrow</span>
            <span>Avançar Etapa do Loop</span>
          </button>
        </div>

        <!-- Interactive Loop Steps Grid -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          @for (step of content.loopSteps; track step.step; let i = $index) {
            <div 
              [class]="currentLoopIndex() === i 
                ? 'bg-[#0A0A0B] ring-2 ring-[#F27D26] scale-[1.02] shadow-xl' 
                : 'bg-[#0A0A0B]/60 opacity-80 hover:opacity-100'"
              class="p-6 rounded-2xl border border-white/10 transition-all space-y-4 relative">
              
              <div class="flex items-center justify-between">
                <span class="text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/20">
                  Fase 0{{ i + 1 }}
                </span>
                @if (currentLoopIndex() === i) {
                  <span class="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-800 animate-pulse">
                    • EM EXECUÇÃO
                  </span>
                }
              </div>

              <div class="flex items-center space-x-3">
                <div class="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#F27D26]">
                  <span class="material-symbols-outlined text-3xl">{{ step.icon }}</span>
                </div>
                <h4 class="text-2xl font-extrabold text-white tracking-wider">{{ step.step }}</h4>
              </div>

              <p class="text-xs text-zinc-300 leading-relaxed">{{ step.description }}</p>

              <!-- Decision banner on APRENDER -->
              @if (i === 2) {
                <div class="p-3 rounded-xl bg-[#F27D26]/10 border border-[#F27D26]/30 text-xs text-zinc-200">
                  <strong class="text-[#F27D26] font-bold block mb-1">Decisão Estratégica:</strong>
                  <strong>Pivotar</strong> (mudar de rota baseado em métricas) ou <strong>Perseverar</strong> (manter e escalar).
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- A Anatomia do MVP -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-8">
        <div class="border-b border-white/10 pb-4">
          <span class="text-xs font-mono font-semibold text-[#F27D26] uppercase tracking-wider block mb-1">
            RESOLVENDO O PROBLEMA DESDE O DIA 1
          </span>
          <h3 class="text-2xl font-extrabold text-white">
            A Anatomia do MVP (Produto Mínimo Viável)
          </h3>
          <p class="text-zinc-400 text-xs mt-1">
            Compare a abordagem errada (entregar partes inutilizáveis) versus a abordagem correta (evoluir soluções completas e funcionais).
          </p>
        </div>

        <!-- Interactive MVP Stepper -->
        <div class="flex items-center justify-between bg-[#0A0A0B] p-2 rounded-xl border border-white/10 text-xs font-semibold overflow-x-auto">
          @for (step of mvpSteps; track step.name; let i = $index) {
            <button 
              type="button"
              (click)="selectedMvpStep.set(i)"
              [class]="selectedMvpStep() === i ? 'bg-[#F27D26] text-black font-bold shadow-md' : 'text-zinc-400 hover:text-white'"
              class="px-4 py-2 rounded-lg transition-all flex items-center space-x-2 shrink-0 cursor-pointer">
              <span class="material-symbols-outlined text-base">{{ step.icon }}</span>
              <span>Iteração {{ i + 1 }}: {{ step.name }}</span>
            </button>
          }
        </div>

        <!-- Detailed Comparison Grid for Selected Step -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Flawed approach -->
          <div class="p-6 rounded-2xl bg-rose-950/20 border border-rose-800/40 space-y-4">
            <div class="flex items-center justify-between text-rose-300">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-rose-400">cancel</span>
                <h4 class="font-bold text-sm uppercase font-mono">Abordagem Incorreta (Por Partes)</h4>
              </div>
              <span class="text-xs font-bold text-rose-400">❌ Cliente Frustrado</span>
            </div>

            <div class="p-4 bg-[#0A0A0B] rounded-xl border border-rose-900/50 text-xs space-y-2">
              <p class="font-bold text-white">Estágio Atual: {{ mvpSteps[selectedMvpStep()].flawedPart }}</p>
              <p class="text-zinc-400">{{ mvpSteps[selectedMvpStep()].flawedDesc }}</p>
            </div>

            <p class="text-xs text-rose-200/80 leading-relaxed">
              {{ content.mvpComparison.error.text }}
            </p>
          </div>

          <!-- Correct approach -->
          <div class="p-6 rounded-2xl bg-emerald-950/20 border border-emerald-800/40 space-y-4">
            <div class="flex items-center justify-between text-emerald-300">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-emerald-400">check_circle</span>
                <h4 class="font-bold text-sm uppercase font-mono">Abordagem Correta (MVP Funcional)</h4>
              </div>
              <span class="text-xs font-bold text-emerald-400">Satisfação Desde o Dia 1</span>
            </div>

            <div class="p-4 bg-[#0A0A0B] rounded-xl border border-emerald-900/50 text-xs space-y-2">
              <p class="font-bold text-white">Solução Entregue: {{ mvpSteps[selectedMvpStep()].name }}</p>
              <p class="text-zinc-400">{{ mvpSteps[selectedMvpStep()].correctDesc }}</p>
            </div>

            <p class="text-xs text-emerald-200/80 leading-relaxed">
              {{ content.mvpComparison.correct.text }}
            </p>
          </div>
        </div>
      </div>
    </section>
  `
})
export class Sprint3SectionComponent {
  content = SPRINT_3_CONTENT;
  currentLoopIndex = signal<number>(0);
  selectedMvpStep = signal<number>(0);

  mvpSteps = [
    {
      name: "Skate (MVP Inicial)",
      icon: "skateboarding",
      flawedPart: "Pneu Único isolado",
      flawedDesc: "Um pneu solto não ajuda o cliente a se locomover nem gera nenhum valor útil.",
      correctDesc: "Um skate simples resolve imediatamente o problema de locomoção de A para B e coleta feedback de estabilidade."
    },
    {
      name: "Patinete (Com Direção)",
      icon: "electric_scooter",
      flawedPart: "Chassis e Eixos montados",
      flawedDesc: "Uma estrutura metálica sem motor ou assento ainda é inutilizável para o cliente final.",
      correctDesc: "Adiciona guidão ao skate. O cliente agora tem controle direcional do produto."
    },
    {
      name: "Bicicleta (Propulsão)",
      icon: "pedal_bike",
      flawedPart: "Carcaça de metal sem motor",
      flawedDesc: "A lataria do carro vazia continua sem capacidade de rodar na rua.",
      correctDesc: "Evolução para tração manual eficiente. Maior velocidade e alcance de locomoção."
    },
    {
      name: "Carro Completo (Escala)",
      icon: "directions_car",
      flawedPart: "Carro montado no final",
      flawedDesc: "Deploy final só ocorre depois de meses/anos de espera do cliente sem feedback intermediário.",
      correctDesc: "O produto atinge o topo de conforto e capacidade, construído em cima de 3 ciclos de feedback de uso real."
    }
  ];

  advanceLoopStep() {
    this.currentLoopIndex.update(idx => (idx + 1) % 3);
  }
}
